import { useState, useCallback, useRef, useEffect } from 'react'
import { useAuth } from '@/modules/auth'
import { RoutesApi } from '@/shared/api/routes'

export interface LocationSearchResult {
  name: string
  address?: string
  latitude: number
  longitude: number
}

export interface LocationSearchOptions {
  /** When true, only parse coordinate pairs; do not call forward geocode. */
  coordinatesOnly?: boolean
}

export interface UseLocationSearchReturn {
  searchQuery: string
  setSearchQuery: (query: string) => void
  searchResult: LocationSearchResult | null
  isSearching: boolean
  centerOnSearch: { latitude: number; longitude: number } | null
  onSearch: (query: string, options?: LocationSearchOptions) => Promise<{ applied: boolean }>
  onClearResult: () => void
  getWaypointData: () => LocationSearchResult | null
}

function tryParseCoordinatePair(trimmed: string): LocationSearchResult | null {
  const coordPattern = /^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/
  const match = trimmed.match(coordPattern)
  if (!match) return null

  const coord1 = parseFloat(match[1])
  const coord2 = parseFloat(match[2])

  let lat: number
  let lng: number
  if (Math.abs(coord1) <= 90 && Math.abs(coord2) <= 180) {
    lat = coord1
    lng = coord2
  } else if (Math.abs(coord1) <= 180 && Math.abs(coord2) <= 90) {
    lng = coord1
    lat = coord2
  } else {
    lat = coord1
    lng = coord2
  }

  if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
    return {
      name: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
      latitude: lat,
      longitude: lng
    }
  }
  return null
}

export function useLocationSearch(): UseLocationSearchReturn {
  const { getAccessToken } = useAuth()
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [searchResult, setSearchResult] = useState<LocationSearchResult | null>(null)
  const [centerOnSearch, setCenterOnSearch] = useState<{ latitude: number; longitude: number } | null>(null)
  const sessionTokenRef = useRef<string | null>(null)

  // Initialize session token
  useEffect(() => {
    if (!sessionTokenRef.current) {
      const storedToken = localStorage.getItem('mapbox_search_session_token')
      if (storedToken) {
        sessionTokenRef.current = storedToken
      } else {
        const newToken = crypto.randomUUID()
        sessionTokenRef.current = newToken
        localStorage.setItem('mapbox_search_session_token', newToken)
      }
    }
  }, [])

  const onSearch = useCallback(async (query: string, options?: LocationSearchOptions) => {
    const trimmed = query.trim()
    if (!trimmed) return { applied: false }

    const coordinatesOnly = options?.coordinatesOnly === true

    setIsSearching(true)
    try {
      const coordResult = tryParseCoordinatePair(trimmed)
      if (coordResult) {
        setCenterOnSearch({ latitude: coordResult.latitude, longitude: coordResult.longitude })
        setSearchResult(coordResult)
        return { applied: true }
      }

      if (coordinatesOnly) {
        setSearchResult(null)
        setCenterOnSearch(null)
        return { applied: false }
      }

      // Fallback to geocoding
      // For map search, we need full details (coordinates) so set returnFullDetails to true
      const routesApi = new RoutesApi(getAccessToken)
      const results = await routesApi.forwardGeocode(
        trimmed, 
        undefined, 
        undefined, 
        1,
        sessionTokenRef.current || undefined,
        true // Need full details for map centering
      )
      
      if (results && results.length > 0) {
        const result = results[0]
        const searchResultData = {
          name: result.name,
          address: result.address,
          latitude: result.latitude,
          longitude: result.longitude
        }
        setCenterOnSearch({ latitude: result.latitude, longitude: result.longitude })
        setSearchResult(searchResultData)
        return { applied: true }
      }

      setSearchResult(null)
      return { applied: false }
    } catch (error) {

      setSearchResult(null)
      return { applied: false }
    } finally {
      setIsSearching(false)
    }
  }, [getAccessToken])

  const onClearResult = useCallback(() => {
    setSearchResult(null)
    setCenterOnSearch(null)
  }, [])

  return {
    searchQuery,
    setSearchQuery,
    searchResult,
    isSearching,
    centerOnSearch,
    onSearch,
    onClearResult,
    getWaypointData: () => searchResult
  }
}

